const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-05-07" → "May 7, 2026". Parsed by hand: `new Date("2026-05-07")` is UTC midnight,
 *  which reads back as the previous day everywhere west of Greenwich. */
export function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "2026-05-07" → "May '26" — for axis ticks, where the day is noise. */
/**
 * ms timestamp → local "YYYY-MM-DD", for `formatDate`. Local rather than
 * `toISOString`, because this reads as "the day this was built" to someone in
 * Chicago and the UTC date rolls over at 6–7pm local.
 */
export function isoLocalDate(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatMonthTick(iso) {
  if (!iso) return '';
  const [y, m] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} '${String(y).slice(2)}`;
}

export function formatShares(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/** $1.24T / $456.7B / $12.35M — compact, with the precision that reads naturally at each scale. */
export function formatMarketCap(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/** $5M / $1.5B — the same scale as formatMarketCap, without the trailing zeros. For a
 *  round figure named in prose or on a rule line, where "$5.00M" reads like a measurement. */
export function formatMarketCapShort(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const [divisor, suffix] = abs >= 1e12 ? [1e12, 'T'] : abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : [1e3, 'K'];
  return `$${Number((n / divisor).toFixed(2))}${suffix}`;
}

/** Compact share counts for a chart axis: 1.24B / 456M / 12.3M. */
export function formatSharesCompact(n) {
  if (n == null || !Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(Math.round(n));
}

export function formatPrice(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

export function formatPercent(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

/** Serialize rows to CSV and hand the browser a download. */
export function downloadCsv(filename, headers, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
